import React from 'react';

import { Box } from '@material-ui/core';
import { format, parse } from 'date-fns';
import { ErrorMessage, FieldErrors } from 'react-hook-form';

import { MultiDatePicker, MultiDatePickerProps } from './MultiDatePicker';

import { TypographyCN } from 'components';
import { useMobile } from 'utils/useMobile';

type MultiDatePickerBaseProps = Omit<MultiDatePickerProps, 'value' | 'onChange'>;

type DateMultiInputProps = MultiDatePickerBaseProps & {
  label?: string;
  sublabel?: string;
  labelProps?: React.ComponentProps<typeof TypographyCN>;
  name?: string;
  errorMessage?: string | FieldErrors<any>;
  boxHeight?: string;
  fontSize?: string;
  /** String no formato 'yyyy-MM-dd,yyyy-MM-dd' */
  value?: string;
  onChange?: (value: string) => void;
};

const SERVER_FORMAT = 'yyyy-MM-dd';

function parseValue(value?: string): Date[] {
  if (!value) return [];
  return value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => parse(s, SERVER_FORMAT, new Date()))
    .filter(d => !Number.isNaN(d.getTime()));
}

function serializeDates(dates: Date[]): string {
  if (!dates.length) return '';
  const ordered = dates.slice().sort((a, b) => a.getTime() - b.getTime());
  return ordered.map(d => format(d, SERVER_FORMAT)).join(',');
}

export function DateMultiInput({
  label,
  sublabel,
  labelProps,
  errorMessage,
  boxHeight,
  fontSize,
  value,
  onChange,
  ...rest
}: DateMultiInputProps) {
  const downBreakPoint = useMobile();

  const dates = React.useMemo(() => parseValue(value), [value]);

  const handleChange = React.useCallback(
    (newDates: Date[]) => {
      const serialized = serializeDates(newDates);
      if (onChange) onChange(serialized);
    },
    [onChange],
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="space-between"
      style={{
        gap: '0.25rem',
        height: boxHeight,
      }}
    >
      {label && (
        <TypographyCN
          component="label"
          variant={downBreakPoint ? 'body1' : 'h6'}
          align="center"
          size="mob-variation-one"
          weight="big"
          color="primary"
          style={{
            fontSize: fontSize,
          }}
          {...labelProps}
        >
          {label}
        </TypographyCN>
      )}
      <MultiDatePicker
        variant="inline"
        format="dd/MM/yyyy"
        label={downBreakPoint ? undefined : sublabel}
        margin="none"
        inputVariant="filled"
        InputProps={{ disableUnderline: !Boolean(errorMessage), error: false }}
        fullWidth
        invalidLabel="Desconhecido"
        invalidDateMessage="Formato de data inválido"
        maxDateMessage="Data não deve ser maior que 01-01-2100"
        minDateMessage="Data não deve ser menor que 01-01-1900"
        value={dates}
        onChange={handleChange}
        {...rest}
      />
      {downBreakPoint && sublabel && (
        <TypographyCN component="span" variant="body2" weight="light" color="primary" style={{ paddingLeft: 8 }}>
          {sublabel}
        </TypographyCN>
      )}
      {errorMessage && (
        <TypographyCN
          component="span"
          variant="body2"
          align="center"
          weight="light"
          color="error"
          style={{ fontSize: 14, fontWeight: 700 }}
        >
          {typeof errorMessage === 'string' ? (
            errorMessage
          ) : (
            <ErrorMessage errors={errorMessage} name={rest.name as string}>
              {({ message }) => message}
            </ErrorMessage>
          )}
        </TypographyCN>
      )}
    </Box>
  );
}
